import React, { useState, useRef, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ScreeningOverviewView } from '../components/ScreeningOverviewView';
import { ProfessionalBelagomLayout } from '../components/ProfessionalBelagomLayout';
import { ProfessionalCrisisPool } from '../components/ProfessionalCrisisPool';
import { ProfessionalWaakvlam } from '../components/ProfessionalWaakvlam';
import { type BelagomView, LEGACY_BELAGOM_VIEWS } from '../components/ProfessionalBelagomSidebar';
import {
  User,
  FolderOpen,
  FileText,
  Download,
  ExternalLink,
  CheckCircle,
  Clock,
  BookOpen,
  ShieldCheck,
  Loader2,
  Briefcase,
  Info,
  Camera,
  GraduationCap,
  Plus,
  Trash2,
  Video,
  Upload,
  CreditCard,
  Coins,
  ThumbsUp,
  ThumbsDown,
  MessageSquare,
  Send,
  KeyRound,
  Scale,
  X,
} from 'lucide-react';
import DOMPurify from 'dompurify';
import api from '../services/api';
import { KlachtenSectie } from '../components/klachten/KlachtenSectie';
import klachtenreglement from '../data/klachtenreglement.json';
import ReisradiusMap from '../components/ReisradiusMap';
import TagSelector from '../components/TagSelector';
import TijdslotAgenda, { type Tijdslot } from '../components/TijdslotAgenda';
import { useProfileTaxonomy } from '../hooks/useProfileTaxonomy';

/** FO stap 3: bio's zijn begrensd op 300 woorden per veld */
const MAX_BIO_WOORDEN = 300;

/** Woorden tellen zoals de backend het doet: witruimte-gescheiden tokens */
const countWords = (text?: string | null) =>
  text ? text.trim().split(/\s+/).filter(Boolean).length : 0;
import { toast } from 'react-hot-toast';
import { useAuthStore } from '../store/authStore';

// ---------------------------------------------------------------------------
// Individual view components
// ---------------------------------------------------------------------------

// ScreeningView is now provided by ScreeningOverviewView (imported above)

interface WorkExperienceItem {
  company: string;
  position: string;
  start_date: string;
  end_date: string;
  current: boolean;
  description: string;
}

interface EducationItem {
  institution: string;
  degree: string;
  field: string;
  start_year: number;
  end_year: number | null;
  current: boolean;
}

const PortfolioView: React.FC = () => {
  const { t } = useTranslation('belagom');
  const { t: tProfile } = useTranslation('profile');
  // FO stap 4/5 — taxonomie voor specialismen, doelgroepen en branches
  const { user } = useAuthStore();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<'persoonlijk' | 'adres' | 'professioneel' | 'ervaring' | 'zichtbaarheid' | 'factuur'>('persoonlijk');
  const [saving, setSaving] = useState(false);
  const [profilePhoto, setProfilePhoto] = useState<string | null>(null);
  const [profilePhotoFile, setProfilePhotoFile] = useState<File | null>(null);
  const photoInputRef = useRef<HTMLInputElement>(null);
  const [introVideo, setIntroVideo] = useState<string | null>(null);
  const [videoUploading, setVideoUploading] = useState(false);
  const videoInputRef = useRef<HTMLInputElement>(null);
  const [screeningTypes, setScreeningTypes] = useState<string[]>([]);
  const [businessForm, setBusinessForm] = useState({
    invoice_bedrijfsnaam: '',
    invoice_kvk_nummer: '',
    invoice_btw_nummer: '',
    invoice_straatnaam: '',
    invoice_huisnummer: '',
    invoice_postcode: '',
    invoice_plaatsnaam: '',
    invoice_iban: '',
  });
  const [businessLogoPreview, setBusinessLogoPreview] = useState<string | null>(null);
  const [businessSaving, setBusinessSaving] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const businessLogoInputRef = useRef<HTMLInputElement>(null);

  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    birth_date: '',
    phone: '',
    email: '',
    introduction: '',
    street_address: '',
    postal_code: '',
    city: '',
    country: 'Nederland',
    years_self_employed: 0,
    kvk_nummer: '',
    kvk_registration_date: '',
    availability_status: 'immediately' as 'not_available' | 'immediately' | 'specific_date',
    available_from_date: '',
    show_employers_on_profile: false,
    // Gesplitste bio (FO stap 3)
    bio_medewerker: '',
    bio_werkgever: '',
    // Introductievideo als YouTube/Vimeo-link (FO stap 7)
    intro_video_url: '',
    languages: ['Nederlands'] as string[],
    active_regions: [] as string[],
    reisradius_km: null as number | null,
    affinity: [] as string[],
    // Expertise- en doelgroeptags (FO stap 4)
    specialisatie_tags: [] as string[],
    doelgroep_niveaus: [] as string[],
    // Wekelijkse tijdslots (FO stap 8) — vervangt uren per week
    availability_slots: [] as Tijdslot[],
    work_experience: [] as WorkExperienceItem[],
    education: [] as EducationItem[],
  });

  const { data: taxonomy } = useProfileTaxonomy();

  const { data: profileData, isLoading } = useQuery({
    queryKey: ['professional-profile-belagom'],
    queryFn: async () => {
      const res = await api.get('/professional/profile');
      const data = res.data?.data;
      const profile = data?.profile ?? data;
      // Tijdslots komen naast het profiel terug (eigen tabel); plat maken zodat
      // het formulier ze net als de andere velden kan inlezen.
      return profile ? { ...profile, availability_slots: data?.availability_slots ?? [] } : profile;
    },
  });

  useEffect(() => {
    if (!profileData) return;
    const p = profileData;
    setForm({
      first_name: p.first_name || '',
      last_name: p.last_name || '',
      birth_date: p.birth_date ? p.birth_date.split('T')[0] : '',
      phone: p.phone || '',
      email: p.email || '',
      introduction: p.introduction || '',
      street_address: p.street_address || '',
      postal_code: p.postal_code || '',
      city: p.city || '',
      country: p.country || 'Nederland',
      years_self_employed: p.years_self_employed || 0,
      kvk_nummer: p.kvk_nummer || '',
      kvk_registration_date: p.kvk_registration_date
        ? new Date(p.kvk_registration_date).toISOString().split('T')[0]
        : '',
      availability_status: p.availability_status || 'immediately',
      available_from_date: p.available_from_date || '',
      show_employers_on_profile: p.show_employers_on_profile || false,
      bio_medewerker: p.bio_medewerker || '',
      bio_werkgever: p.bio_werkgever || '',
      intro_video_url: p.intro_video_url || '',
      languages: Array.isArray(p.languages) ? p.languages : ['Nederlands'],
      active_regions: Array.isArray(p.active_regions) ? p.active_regions : [],
      reisradius_km: p.reisradius_km ?? null,
      affinity: Array.isArray(p.affinity)
        ? p.affinity
        : Array.isArray(p.industry_experience)
        ? p.industry_experience
        : [],
      work_experience: Array.isArray(p.work_experience) ? p.work_experience : [],
      education: Array.isArray(p.education) ? p.education : [],
      specialisatie_tags: Array.isArray(p.specialisatie_tags) ? p.specialisatie_tags : [],
      doelgroep_niveaus: Array.isArray(p.doelgroep_niveaus) ? p.doelgroep_niveaus : [],
      availability_slots: Array.isArray(p.availability_slots) ? p.availability_slots : [],
    });
    if (p.profile_photo_path) {
      const path = p.profile_photo_path as string;
      setProfilePhoto(path.startsWith('/') ? path : `/${path}`);
    }
    if (p.intro_video_path) {
      const vp = p.intro_video_path as string;
      setIntroVideo(vp.startsWith('/') ? vp : `/uploads/${vp.replace(/^uploads\//, '')}`);
    }
    if (Array.isArray(p.professional_types_from_screening)) {
      setScreeningTypes(p.professional_types_from_screening);
    }
  }, [profileData]);

  // Fetch grouped rate structures for adviestarief display
  const { data: groupedRatesData } = useQuery({
    queryKey: ['professional-rates-grouped-belagom'],
    queryFn: async () => {
      const response = await api.get('/professional-rates/grouped');
      return response.data.data;
    },
    enabled: screeningTypes.length > 0,
  });

  const { data: businessProfileData } = useQuery({
    queryKey: ['professional-business-profile'],
    queryFn: async () => {
      const res = await api.get('/professional/business-profile');
      return res.data?.data ?? {};
    },
  });

  useEffect(() => {
    if (!businessProfileData) return;
    const b = businessProfileData;
    setBusinessForm({
      invoice_bedrijfsnaam: b.invoice_bedrijfsnaam || '',
      invoice_kvk_nummer: b.invoice_kvk_nummer || '',
      invoice_btw_nummer: b.invoice_btw_nummer || '',
      invoice_straatnaam: b.invoice_straatnaam || '',
      invoice_huisnummer: b.invoice_huisnummer || '',
      invoice_postcode: b.invoice_postcode || '',
      invoice_plaatsnaam: b.invoice_plaatsnaam || '',
      invoice_iban: b.invoice_iban || '',
    });
    if (b.invoice_logo_url) {
      const p = b.invoice_logo_url as string;
      setBusinessLogoPreview(p.startsWith('/') ? p : `/uploads/${p}`);
    }
  }, [businessProfileData]);

  const setBiz = (field: keyof typeof businessForm, value: string) =>
    setBusinessForm(f => ({ ...f, [field]: value }));

  const handleBusinessLogoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error(t('toast.photoTooLarge'));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setBusinessLogoPreview(reader.result as string);
    reader.readAsDataURL(file);

    setLogoUploading(true);
    try {
      const fd = new FormData();
      fd.append('logo', file);
      await api.post('/professional/business-profile/logo', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      queryClient.invalidateQueries({ queryKey: ['professional-business-profile'] });
      toast.success(t('toast.logoUploaded'));
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(msg || t('toast.logoUploadFailed'));
    } finally {
      setLogoUploading(false);
    }
  };

  const handleSaveBusinessProfile = async () => {
    setBusinessSaving(true);
    try {
      await api.put('/professional/business-profile', businessForm);
      queryClient.invalidateQueries({ queryKey: ['professional-business-profile'] });
      toast.success(t('toast.businessProfileSaved'));
    } catch (err: unknown) {
      const data = (err as { response?: { data?: { message?: string; errors?: string[] } } })?.response?.data;
      toast.error(data?.errors?.[0] || data?.message || t('toast.businessProfileSaveFailed'));
    } finally {
      setBusinessSaving(false);
    }
  };

  const calcYearsFromDate = (d: string): number => {
    if (!d) return 0;
    const reg = new Date(d);
    const now = new Date();
    let y = now.getFullYear() - reg.getFullYear();
    const md = now.getMonth() - reg.getMonth();
    if (md < 0 || (md === 0 && now.getDate() < reg.getDate())) y--;
    return Math.max(0, y);
  };

  const experienceLevelFromYears = (y: number): '0-2' | '2-5' | '5-10' | '10+' => {
    if (y >= 10) return '10+';
    if (y >= 5) return '5-10';
    if (y >= 2) return '2-5';
    return '0-2';
  };

  const kvkYears = calcYearsFromDate(form.kvk_registration_date);
  const kvkLevel = experienceLevelFromYears(kvkYears);

  // Work experience handlers
  const addWorkExperience = () => setForm(f => ({
    ...f,
    work_experience: [...f.work_experience, { company: '', position: '', start_date: '', end_date: '', current: false, description: '' }],
  }));
  const removeWorkExperience = (i: number) => setForm(f => ({
    ...f,
    work_experience: f.work_experience.filter((_, idx) => idx !== i),
  }));
  const updateWorkExperience = (i: number, field: keyof WorkExperienceItem, value: unknown) => setForm(f => ({
    ...f,
    work_experience: f.work_experience.map((w, idx) => idx === i ? { ...w, [field]: value } : w),
  }));

  // Education handlers
  const addEducation = () => setForm(f => ({
    ...f,
    education: [...f.education, { institution: '', degree: '', field: '', start_year: new Date().getFullYear(), end_year: null, current: false }],
  }));
  const removeEducation = (i: number) => setForm(f => ({
    ...f,
    education: f.education.filter((_, idx) => idx !== i),
  }));
  const updateEducation = (i: number, field: keyof EducationItem, value: unknown) => setForm(f => ({
    ...f,
    education: f.education.map((e, idx) => idx === i ? { ...e, [field]: value } : e),
  }));

  // Video upload
  const handleVideoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 100 * 1024 * 1024) {
      toast.error(t('toast.videoTooLarge'));
      return;
    }
    const allowed = ['video/mp4', 'video/webm', 'video/quicktime', 'video/x-msvideo'];
    if (!allowed.includes(file.type)) {
      toast.error(t('toast.invalidVideoFormat'));
      return;
    }
    setVideoUploading(true);
    try {
      const fd = new FormData();
      fd.append('intro_video', file);
      const res = await api.post('/professional/intro-video', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const path = res.data?.data?.intro_video_path;
      if (path) setIntroVideo(`/uploads/${path}`);
      toast.success(t('toast.videoUploaded'));
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(msg || t('toast.uploadFailed'));
    } finally {
      setVideoUploading(false);
    }
  };

  const handleVideoDelete = async () => {
    try {
      await api.delete('/professional/intro-video');
      setIntroVideo(null);
      toast.success(t('toast.videoDeleted'));
    } catch {
      toast.error(t('toast.deleteFailed'));
    }
  };

  const set = (field: string, value: unknown) => setForm(f => ({ ...f, [field]: value }));

  const toggleArrayItem = (field: 'languages' | 'active_regions', item: string) => {
    setForm(f => {
      const arr = f[field] as string[];
      return {
        ...f,
        [field]: arr.includes(item) ? arr.filter(x => x !== item) : [...arr, item],
      };
    });
  };

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error(t('toast.photoTooLarge'));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setProfilePhoto(reader.result as string);
    reader.readAsDataURL(file);
    setProfilePhotoFile(file);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const fd = new FormData();
      (Object.entries(form) as [string, unknown][]).forEach(([k, v]) => {
        if (Array.isArray(v)) {
          fd.append(k, JSON.stringify(v));
        } else if (typeof v === 'boolean') {
          fd.append(k, v ? 'true' : 'false');
        } else {
          fd.append(k, String(v));
        }
      });
      if (profilePhotoFile) fd.append('profile_photo', profilePhotoFile);
      await api.post('/professional/profile', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      queryClient.invalidateQueries({ queryKey: ['professional-profile-belagom'] });
      toast.success(t('toast.profileSaved'));
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(msg || t('toast.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const ALL_LANGUAGES = t('constant.languages', { returnObjects: true }) as string[];
  const TABS = [
    { id: 'persoonlijk' as const, label: t('tabs.personal') },
    { id: 'adres' as const, label: t('tabs.addressAvailability') },
    { id: 'professioneel' as const, label: t('tabs.professional') },
    { id: 'ervaring' as const, label: t('tabs.experienceEducation') },
    { id: 'zichtbaarheid' as const, label: t('tabs.videoVisibility') },
    { id: 'factuur' as const, label: t('tabs.invoicing') },
  ];

  const TYPE_LABELS: Record<string, string> = {
    'bedrijfsmaatschappelijk_werker': 'Bedrijfsmaatschappelijk Werker',
    'vertrouwenspersoon': 'Vertrouwenspersoon',
    'mediator': 'Mediator',
    'psycholoog_coach': 'Psycholoog-Coach',
    'financieel_coach': 'Financieel Coach',
    'loopbaancoach': 'Loopbaancoach',
    'teamcoach': 'Teamcoach',
    'vitaliteitscoach': 'Vitaliteitscoach',
    'burnout_coach': 'Burn-out Coach',
  };

  const EXP_LEVEL_LABELS: Record<string, string> = {
    '0-2': t('constant.experienceLevel.starter'),
    '2-5': t('constant.experienceLevel.experienced'),
    '5-10': t('constant.experienceLevel.advanced'),
    '10+': t('constant.experienceLevel.expert'),
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <Loader2 size={20} className="animate-spin text-[#0B1F3A]" />
      </div>
    );
  }

  const inputCls =
    'w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#0B1F3A]/20 focus:border-[#0B1F3A]';
  const labelCls = 'block text-xs font-semibold text-gray-600 mb-1';

  return (
    <div className="w-full space-y-6">
      {/* Profielfoto */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-widest mb-4">{t('section.profilePhoto')}</h2>
        <div className="flex flex-wrap items-center gap-5">
          <div className="relative" style={{ width: 96, height: 96 }}>
            {/* Belagom spinning logo als frame, originele kleuren blauw/geel */}
            <img
              src="/belagom-symbol.png"
              alt=""
              style={{
                position: 'absolute',
                inset: 0,
                width: 96,
                height: 96,
                pointerEvents: 'none',
              }}
            />
            {/* Profielfoto gecentreerd in het logo */}
            <div
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: 60,
                height: 60,
                borderRadius: '50%',
                overflow: 'hidden',
                backgroundColor: '#f3f4f6',
                border: '2px solid #fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {profilePhoto ? (
                <img src={profilePhoto} alt={t('section.profilePhoto')} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                <User size={22} className="text-gray-400" />
              )}
            </div>
            {/* Camera-knop */}
            <button
              type="button"
              onClick={() => photoInputRef.current?.click()}
              className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-[#0B1F3A] text-white flex items-center justify-center shadow-sm hover:opacity-90"
              style={{ zIndex: 10 }}
            >
              <Camera size={14} />
            </button>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-800">
              {form.first_name || user?.first_name} {form.last_name || user?.last_name}
            </p>
            <button
              type="button"
              onClick={() => photoInputRef.current?.click()}
              className="text-xs text-[#0B1F3A] hover:underline mt-1"
            >
              {t('button.changePhoto')}
            </button>
            <p className="text-xs text-gray-400 mt-0.5">{t('info.photoFormat')}</p>
          </div>
          {/* Bedrijf van de professional (uit Factuurgegevens) */}
          {(businessForm.invoice_bedrijfsnaam || businessLogoPreview) && (
            <div className="flex items-center gap-4 pl-5 border-l border-gray-200">
              {businessLogoPreview && (
                <div className="w-16 h-16 rounded-lg border border-gray-200 bg-white overflow-hidden flex items-center justify-center flex-shrink-0">
                  <img src={businessLogoPreview} alt={t('label.logo')} className="w-full h-full object-contain" />
                </div>
              )}
              {businessForm.invoice_bedrijfsnaam && (
                <div>
                  <p className="text-xs text-gray-400">{t('label.companyName')}</p>
                  <p className="text-sm font-medium text-gray-800">{businessForm.invoice_bedrijfsnaam}</p>
                </div>
              )}
            </div>
          )}
          <input
            ref={photoInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={handlePhotoChange}
            className="hidden"
          />
        </div>
      </div>

      {/* Tabbladen */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="flex border-b border-gray-200">
          {TABS.map(tab => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? 'text-[#0B1F3A] border-b-2 border-[#0B1F3A] bg-blue-50/30'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="p-6 space-y-4">
          {/* ── Persoonlijk ── */}
          {activeTab === 'persoonlijk' && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>{t('label.firstName')} <span className="text-red-400">*</span></label>
                  <input
                    type="text"
                    className={inputCls}
                    value={form.first_name}
                    onChange={e => set('first_name', e.target.value)}
                    placeholder={t('placeholder.firstName')}
                  />
                </div>
                <div>
                  <label className={labelCls}>{t('label.lastName')} <span className="text-red-400">*</span></label>
                  <input
                    type="text"
                    className={inputCls}
                    value={form.last_name}
                    onChange={e => set('last_name', e.target.value)}
                    placeholder={t('placeholder.lastName')}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>{t('label.birthDate')} <span className="text-red-400">*</span></label>
                  <input
                    type="date"
                    className={inputCls}
                    value={form.birth_date}
                    onChange={e => set('birth_date', e.target.value)}
                  />
                </div>
                <div>
                  <label className={labelCls}>{t('label.phoneNumber')} <span className="text-red-400">*</span></label>
                  <input
                    type="tel"
                    className={inputCls}
                    value={form.phone}
                    onChange={e => set('phone', e.target.value)}
                    placeholder={t('placeholder.phone')}
                  />
                </div>
              </div>
              <div>
                <label className={labelCls}>{t('label.email')} <span className="text-red-400">*</span></label>
                <input
                  type="email"
                  className={inputCls}
                  value={form.email}
                  onChange={e => set('email', e.target.value)}
                  placeholder={t('placeholder.email')}
                />
              </div>
              <div>
                <label className={labelCls}>{t('label.shortIntroduction')}</label>
                <textarea
                  className={`${inputCls} resize-none`}
                  rows={6}
                  value={form.introduction}
                  onChange={e => set('introduction', e.target.value)}
                  placeholder={t('placeholder.introductionHint')}
                  maxLength={750}
                />
                <p className="text-xs text-gray-400 mt-1 text-right">{form.introduction.length}/750</p>
              </div>

              {/* Gesplitste bio (FO stap 3): één tekst voor de medewerker,
                  één voor de HR-manager. */}
              <div>
                <label className={labelCls}>{tProfile('label.bioMedewerker')}</label>
                <p className="text-xs text-gray-500 mb-2">{tProfile('info.bioMedewerkerDesc')}</p>
                <textarea
                  className={`${inputCls} resize-none`}
                  rows={5}
                  value={form.bio_medewerker}
                  onChange={e => set('bio_medewerker', e.target.value)}
                  placeholder={tProfile('placeholder.bioMedewerker')}
                />
                <p className={`text-xs mt-1 text-right ${countWords(form.bio_medewerker) > MAX_BIO_WOORDEN ? 'text-red-500' : 'text-gray-400'}`}>
                  {countWords(form.bio_medewerker)}/{MAX_BIO_WOORDEN}
                </p>
              </div>

              <div>
                <label className={labelCls}>{tProfile('label.bioWerkgever')}</label>
                <p className="text-xs text-gray-500 mb-2">{tProfile('info.bioWerkgeverDesc')}</p>
                <textarea
                  className={`${inputCls} resize-none`}
                  rows={5}
                  value={form.bio_werkgever}
                  onChange={e => set('bio_werkgever', e.target.value)}
                  placeholder={tProfile('placeholder.bioWerkgever')}
                />
                <p className={`text-xs mt-1 text-right ${countWords(form.bio_werkgever) > MAX_BIO_WOORDEN ? 'text-red-500' : 'text-gray-400'}`}>
                  {countWords(form.bio_werkgever)}/{MAX_BIO_WOORDEN}
                </p>
              </div>
            </>
          )}

          {/* ── Adres & beschikbaarheid ── */}
          {activeTab === 'adres' && (
            <>
              <div>
                <label className={labelCls}>{t('label.streetAddress')} <span className="text-red-400">*</span></label>
                <input
                  type="text"
                  className={inputCls}
                  value={form.street_address}
                  onChange={e => set('street_address', e.target.value)}
                  placeholder={t('placeholder.streetAddress')}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>{t('label.postalCode')} <span className="text-red-400">*</span></label>
                  <input
                    type="text"
                    className={inputCls}
                    value={form.postal_code}
                    onChange={e => set('postal_code', e.target.value)}
                    placeholder={t('placeholder.postalCode')}
                  />
                </div>
                <div>
                  <label className={labelCls}>{t('label.city')} <span className="text-red-400">*</span></label>
                  <input
                    type="text"
                    className={inputCls}
                    value={form.city}
                    onChange={e => set('city', e.target.value)}
                    placeholder={t('placeholder.city')}
                  />
                </div>
              </div>
              <div>
                <label className={labelCls}>{t('label.country')}</label>
                <input
                  type="text"
                  className={inputCls}
                  value={form.country}
                  onChange={e => set('country', e.target.value)}
                  placeholder={t('placeholder.country')}
                />
              </div>

              <hr className="border-gray-100" />

              <div>
                <label className={labelCls}>{t('label.availability')} <span className="text-red-400">*</span></label>
                <div className="grid grid-cols-3 gap-2 mt-1">
                  {[
                    { value: 'immediately', label: t('constant.availability.immediately') },
                    { value: 'specific_date', label: t('constant.availability.specificDate') },
                    { value: 'not_available', label: t('constant.availability.notAvailable') },
                  ].map(opt => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => set('availability_status', opt.value)}
                      className={`px-3 py-2 rounded-lg border text-xs font-medium transition-all ${
                        form.availability_status === opt.value
                          ? 'border-[#0B1F3A] bg-[#0B1F3A] text-white'
                          : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {form.availability_status === 'specific_date' && (
                <div>
                  <label className={labelCls}>{t('label.availableFrom')}</label>
                  <input
                    type="date"
                    className={inputCls}
                    value={form.available_from_date}
                    onChange={e => set('available_from_date', e.target.value)}
                  />
                </div>
              )}

              {/* Tijdslots vervangen 'uren per week' (Wet DBA, FO stap 8) */}
              <div>
                <label className={labelCls}>{tProfile('label.timeslots')}</label>
                <p className="text-xs text-gray-500 mb-2">{tProfile('info.timeslotsDesc')}</p>
                <TijdslotAgenda
                  slots={form.availability_slots}
                  onChange={(slots) => set('availability_slots', slots)}
                  minSlots={taxonomy?.min_tijdslots ?? 3}
                />
              </div>
            </>
          )}

          {/* ── Professioneel ── */}
          {activeTab === 'professioneel' && (
            <>
              {/* Gescreende rollen */}
              {screeningTypes.length > 0 && (
                <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <ShieldCheck size={16} className="text-[#0B1F3A]" />
                    <span className="text-sm font-semibold text-[#0B1F3A]">{t('section.screenedRoles')}</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {screeningTypes.map(st => (
                      <span key={st} className="px-3 py-1 rounded-full text-xs font-medium bg-[#0B1F3A] text-white">
                        {TYPE_LABELS[st] || st}
                      </span>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mt-2">Deze rollen komen uit je screening en zijn niet bewerkbaar.</p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>{t('label.kvkNumber')}</label>
                  <input
                    type="text"
                    className={inputCls}
                    value={form.kvk_nummer}
                    onChange={e => set('kvk_nummer', e.target.value)}
                    placeholder={t('placeholder.kvkNumber')}
                    maxLength={8}
                  />
                </div>
                <div>
                  <label className={labelCls}>{t('label.kvkRegistrationDate')}</label>
                  <input
                    type="date"
                    className={inputCls}
                    value={form.kvk_registration_date}
                    onChange={e => set('kvk_registration_date', e.target.value)}
                  />
                </div>
              </div>
              <div>
                <label className={labelCls}>{t('label.yearsSelfEmployed')} <span className="text-red-400">*</span></label>
                <input
                  type="number"
                  min={0}
                  className={inputCls}
                  value={form.years_self_employed}
                  onChange={e => set('years_self_employed', parseInt(e.target.value) || 0)}
                />
              </div>

              {/* Marktconform adviestarief */}
              {screeningTypes.length > 0 && form.kvk_registration_date && groupedRatesData && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center gap-2 mb-3">
                    <Info size={16} className="text-green-700" />
                    <span className="text-sm font-semibold text-green-800">
                      {t('section.marketRateAdvice')} ({EXP_LEVEL_LABELS[kvkLevel]}, {kvkYears} jaar KVK)
                    </span>
                  </div>
                  <div className="space-y-3">
                    {screeningTypes.map(type => {
                      const td = groupedRatesData.find((g: { type: string }) => g.type === type);
                      if (!td?.rates) return null;
                      const r = td.rates.find((x: { experience_level: string }) => x.experience_level === kvkLevel);
                      if (!r) return null;
                      const base = Number(r.base_rate);
                      const final = Number(r.final_rate);
                      const belagom = final - base;
                      return (
                        <div key={type} className="bg-white rounded-lg border border-green-100 p-3">
                          <p className="text-xs font-semibold text-gray-900 mb-2">{TYPE_LABELS[type] || type}</p>
                          <div className="grid grid-cols-3 gap-2 text-xs">
                            <div className="bg-green-50 rounded p-2">
                              <div className="text-gray-600">{t('info.yourRate')}</div>
                              <div className="text-lg font-bold text-green-700">&euro;{base.toFixed(2)}</div>
                            </div>
                            <div className="bg-blue-50 rounded p-2">
                              <div className="text-gray-600">{t('info.belagomFee')}</div>
                              <div className="text-lg font-bold text-[#0B1F3A]">&euro;{belagom.toFixed(2)}</div>
                            </div>
                            <div className="bg-gray-50 rounded p-2">
                              <div className="text-gray-600">{t('info.customerPays')}</div>
                              <div className="text-lg font-bold text-gray-900">&euro;{final.toFixed(2)}</div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <p className="text-xs text-gray-500 mt-3">{t('info.rateScaling')}</p>
                </div>
              )}
              {screeningTypes.length > 0 && !form.kvk_registration_date && (
                <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-xs text-yellow-800">{t('info.fillKVKForRate')}</p>
                </div>
              )}

              <hr className="border-gray-100" />

              <div>
                <label className={labelCls}>{t('label.languages')}</label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {ALL_LANGUAGES.map(lang => (
                    <button
                      key={lang}
                      type="button"
                      onClick={() => toggleArrayItem('languages', lang)}
                      className={`px-3 py-1.5 rounded-full border text-xs font-medium transition-all ${
                        form.languages.includes(lang)
                          ? 'border-[#0B1F3A] bg-[#0B1F3A] text-white'
                          : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                      }`}
                    >
                      {lang}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className={labelCls}>{tProfile('reisradius.title')}</label>
                <p className="text-xs text-gray-500 mb-2">{tProfile('reisradius.description')}</p>
                <ReisradiusMap
                  radiusKm={form.reisradius_km}
                  onRadiusChange={(km) => set('reisradius_km', km)}
                />
              </div>

              {/* Specialisme-tags (FO stap 4) */}
              <div>
                <label className={labelCls}>{tProfile('label.specialisations')}</label>
                <p className="text-xs text-gray-500 mb-2">{tProfile('info.specialisationsDesc')}</p>
                <TagSelector
                  options={taxonomy?.specialisatie_tags || []}
                  value={form.specialisatie_tags}
                  onChange={(value) => set('specialisatie_tags', value)}
                  max={taxonomy?.max_specialisatie_tags ?? 8}
                  placeholder={tProfile('placeholder.selectSpecialisation')}
                />
              </div>

              {/* Doelgroep-niveaus (FO stap 4) */}
              <div>
                <label className={labelCls}>{tProfile('label.targetGroups')}</label>
                <p className="text-xs text-gray-500 mb-2">{tProfile('info.targetGroupsDesc')}</p>
                <TagSelector
                  options={taxonomy?.doelgroep_niveaus || []}
                  value={form.doelgroep_niveaus}
                  onChange={(value) => set('doelgroep_niveaus', value)}
                  variant="checkbox"
                />
              </div>

              {/* Branche-affiniteit (FO stap 5) */}
              <div>
                <label className={labelCls}>{tProfile('label.industries')}</label>
                <p className="text-xs text-gray-500 mb-2">{tProfile('info.industriesDesc')}</p>
                <TagSelector
                  options={taxonomy?.branche_affiniteiten || []}
                  value={form.affinity}
                  onChange={(value) => set('affinity', value)}
                  variant="checkbox"
                />
              </div>
            </>
          )}

          {/* ── Ervaring & Opleiding ── */}
          {activeTab === 'ervaring' && (
            <>
              {/* Werkervaring */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Briefcase size={16} className="text-[#0B1F3A]" />
                    <h3 className="text-sm font-semibold text-gray-800">{t('section.workExperience')}</h3>
                  </div>
                  <button
                    type="button"
                    onClick={addWorkExperience}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-[#0B1F3A] border border-[#0B1F3A] rounded-lg hover:bg-blue-50"
                  >
                    <Plus size={14} /> {t('button.add')}
                  </button>
                </div>
                {form.work_experience.length === 0 && (
                  <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-xs text-yellow-800">
                    {t('info.noWorkExperience')}
                  </div>
                )}
                <div className="space-y-3">
                  {form.work_experience.map((w, i) => (
                    <div key={i} className="border border-gray-200 rounded-lg p-3 space-y-2">
                      <div className="flex justify-between items-start">
                        <span className="text-xs font-medium text-gray-500">{t('label.experienceEntry', { number: i + 1 })}</span>
                        <button type="button" onClick={() => removeWorkExperience(i)} className="text-red-500 hover:text-red-700">
                          <Trash2 size={14} />
                        </button>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className={labelCls}>{t('label.company')} *</label>
                          <input type="text" className={inputCls} value={w.company} onChange={e => updateWorkExperience(i, 'company', e.target.value)} />
                        </div>
                        <div>
                          <label className={labelCls}>{t('label.position')} *</label>
                          <input type="text" className={inputCls} value={w.position} onChange={e => updateWorkExperience(i, 'position', e.target.value)} />
                        </div>
                        <div>
                          <label className={labelCls}>{t('label.startDate')} *</label>
                          <input type="date" className={inputCls} value={w.start_date} onChange={e => updateWorkExperience(i, 'start_date', e.target.value)} />
                        </div>
                        <div>
                          <label className={labelCls}>{t('label.endDate')}</label>
                          <input type="date" className={inputCls} value={w.end_date} onChange={e => updateWorkExperience(i, 'end_date', e.target.value)} disabled={w.current} />
                          <label className="flex items-center gap-1 mt-1 text-xs text-gray-600">
                            <input type="checkbox" checked={w.current} onChange={e => updateWorkExperience(i, 'current', e.target.checked)} />
                            {t('label.currentPosition')}
                          </label>
                        </div>
                      </div>
                      <div>
                        <label className={labelCls}>{t('label.description')} *</label>
                        <textarea className={`${inputCls} resize-none`} rows={2} value={w.description} onChange={e => updateWorkExperience(i, 'description', e.target.value)} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <hr className="border-gray-100" />

              {/* Opleidingen */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <GraduationCap size={16} className="text-[#0B1F3A]" />
                    <h3 className="text-sm font-semibold text-gray-800">{t('section.education')}</h3>
                  </div>
                  <button
                    type="button"
                    onClick={addEducation}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-[#0B1F3A] border border-[#0B1F3A] rounded-lg hover:bg-blue-50"
                  >
                    <Plus size={14} /> {t('button.add')}
                  </button>
                </div>
                {form.education.length === 0 && (
                  <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-xs text-yellow-800">
                    {t('info.noEducation')}
                  </div>
                )}
                <div className="space-y-3">
                  {form.education.map((ed, i) => (
                    <div key={i} className="border border-gray-200 rounded-lg p-3 space-y-2">
                      <div className="flex justify-between items-start">
                        <span className="text-xs font-medium text-gray-500">{t('label.educationEntry', { number: i + 1 })}</span>
                        <button type="button" onClick={() => removeEducation(i)} className="text-red-500 hover:text-red-700">
                          <Trash2 size={14} />
                        </button>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className={labelCls}>{t('label.educationInstitution')} *</label>
                          <input type="text" className={inputCls} value={ed.institution} onChange={e => updateEducation(i, 'institution', e.target.value)} />
                        </div>
                        <div>
                          <label className={labelCls}>{t('label.degree')} *</label>
                          <input type="text" className={inputCls} value={ed.degree} onChange={e => updateEducation(i, 'degree', e.target.value)} />
                        </div>
                        <div>
                          <label className={labelCls}>{t('label.fieldOfStudy')} *</label>
                          <input type="text" className={inputCls} value={ed.field} onChange={e => updateEducation(i, 'field', e.target.value)} />
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className={labelCls}>{t('label.startYear')} *</label>
                            <input type="number" min={1950} className={inputCls} value={ed.start_year} onChange={e => updateEducation(i, 'start_year', parseInt(e.target.value) || new Date().getFullYear())} />
                          </div>
                          <div>
                            <label className={labelCls}>{t('label.endYear')}</label>
                            <input type="number" className={inputCls} value={ed.end_year || ''} onChange={e => updateEducation(i, 'end_year', e.target.value ? parseInt(e.target.value) : null)} disabled={ed.current} />
                          </div>
                        </div>
                      </div>
                      <label className="flex items-center gap-1 text-xs text-gray-600">
                        <input type="checkbox" checked={ed.current} onChange={e => updateEducation(i, 'current', e.target.checked)} />
                        {t('label.currentEducation')}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* ── Video & Zichtbaarheid ── */}
          {activeTab === 'zichtbaarheid' && (
            <>
              {/* Intro video */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Video size={16} className="text-[#0B1F3A]" />
                  <h3 className="text-sm font-semibold text-gray-800">{t('section.introVideo')}</h3>
                </div>
                <p className="text-xs text-gray-500 mb-3">
                  {t('info.videoUploadHint')}
                </p>
                {introVideo ? (
                  <div className="space-y-3">
                    <video src={introVideo} controls className="w-full max-w-md rounded-lg border border-gray-200" style={{ maxHeight: 300 }} />
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => videoInputRef.current?.click()}
                        disabled={videoUploading}
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-[#0B1F3A] border border-[#0B1F3A] rounded-lg hover:bg-blue-50 disabled:opacity-50"
                      >
                        <Upload size={14} /> {t('button.replaceVideo')}
                      </button>
                      <button
                        type="button"
                        onClick={handleVideoDelete}
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-red-600 border border-red-300 rounded-lg hover:bg-red-50"
                      >
                        <Trash2 size={14} /> {t('button.deleteVideo')}
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => videoInputRef.current?.click()}
                    disabled={videoUploading}
                    className="w-full max-w-md border-2 border-dashed border-gray-300 rounded-lg px-6 py-6 text-center hover:border-[#0B1F3A] hover:bg-blue-50 transition-colors disabled:opacity-50"
                  >
                    <Video size={32} className="mx-auto text-gray-400 mb-2" />
                    <div className="text-sm font-medium text-gray-700">
                      {videoUploading ? t('button.uploading') : t('button.uploadIntroVideo')}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">{t('info.videoFormats')}</div>
                  </button>
                )}
                <input
                  ref={videoInputRef}
                  type="file"
                  accept="video/mp4,video/webm,video/quicktime,video/x-msvideo"
                  onChange={handleVideoChange}
                  className="hidden"
                />

                {/* Alternatief voor de upload: YouTube/Vimeo-link (FO stap 7) */}
                <div className="mt-4">
                  <label className={labelCls}>{tProfile('label.videoUrl')}</label>
                  <input
                    type="url"
                    className={inputCls}
                    value={form.intro_video_url}
                    onChange={e => set('intro_video_url', e.target.value)}
                    placeholder="https://www.youtube.com/watch?v=..."
                  />
                </div>
              </div>

              <hr className="border-gray-100" />

              {/* Opdrachtgever zichtbaarheid */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Briefcase size={16} className="text-[#0B1F3A]" />
                  <h3 className="text-sm font-semibold text-gray-800">{t('section.employerVisibility')}</h3>
                </div>
                <label className="flex items-start gap-3 p-4 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
                  <div className="relative mt-1">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={form.show_employers_on_profile}
                      onChange={e => set('show_employers_on_profile', e.target.checked)}
                    />
                    <div className="w-9 h-5 bg-gray-200 rounded-full peer peer-checked:bg-[#0B1F3A] after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-4" />
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-medium text-gray-800">{t('label.showEmployers')}</div>
                    <p className="text-xs text-gray-600 mt-1">
                      {t('info.employerVisibilityHint')}
                    </p>
                  </div>
                </label>
              </div>
            </>
          )}

          {/* ── Factuurgegevens ── */}
          {activeTab === 'factuur' && (
            <>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <FileText size={16} className="text-[#0B1F3A]" />
                  <h3 className="text-sm font-semibold text-gray-800">{t('section.invoicingDetails')}</h3>
                </div>
                <p className="text-xs text-gray-500 mb-3">{t('info.invoicingHint')}</p>
              </div>

              <div className="flex items-center gap-4">
                <div
                  className="w-16 h-16 rounded-lg bg-gray-100 border border-gray-200 flex items-center justify-center overflow-hidden shrink-0"
                >
                  {businessLogoPreview ? (
                    <img src={businessLogoPreview} alt={t('label.logo')} className="w-full h-full object-contain" />
                  ) : (
                    <Camera size={20} className="text-gray-400" />
                  )}
                </div>
                <div>
                  <button
                    type="button"
                    onClick={() => businessLogoInputRef.current?.click()}
                    disabled={logoUploading}
                    className="text-xs font-medium text-[#0B1F3A] hover:underline disabled:opacity-50"
                  >
                    {logoUploading ? t('button.uploading') : (businessLogoPreview ? t('button.replaceLogo') : t('button.uploadLogo'))}
                  </button>
                  <p className="text-xs text-gray-400 mt-0.5">{t('info.logoFormat')}</p>
                </div>
                <input
                  ref={businessLogoInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handleBusinessLogoChange}
                  className="hidden"
                />
              </div>

              <div>
                <label className={labelCls}>{t('label.companyName')} <span className="text-red-400">*</span></label>
                <input
                  type="text"
                  className={inputCls}
                  value={businessForm.invoice_bedrijfsnaam}
                  onChange={e => setBiz('invoice_bedrijfsnaam', e.target.value)}
                  placeholder={t('placeholder.companyName')}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>{t('label.kvkNumber')} <span className="text-red-400">*</span></label>
                  <input
                    type="text"
                    className={inputCls}
                    value={businessForm.invoice_kvk_nummer}
                    onChange={e => setBiz('invoice_kvk_nummer', e.target.value)}
                    placeholder={t('placeholder.kvkNumber')}
                  />
                </div>
                <div>
                  <label className={labelCls}>{t('label.btwNumber')} <span className="text-red-400">*</span></label>
                  <input
                    type="text"
                    className={inputCls}
                    value={businessForm.invoice_btw_nummer}
                    onChange={e => setBiz('invoice_btw_nummer', e.target.value)}
                    placeholder={t('placeholder.btwNumber')}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>{t('label.streetName')} <span className="text-red-400">*</span></label>
                  <input
                    type="text"
                    className={inputCls}
                    value={businessForm.invoice_straatnaam}
                    onChange={e => setBiz('invoice_straatnaam', e.target.value)}
                    placeholder={t('placeholder.streetName')}
                  />
                </div>
                <div>
                  <label className={labelCls}>{t('label.houseNumber')} <span className="text-red-400">*</span></label>
                  <input
                    type="text"
                    className={inputCls}
                    value={businessForm.invoice_huisnummer}
                    onChange={e => setBiz('invoice_huisnummer', e.target.value)}
                    placeholder={t('placeholder.houseNumber')}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>{t('label.postalCode')} <span className="text-red-400">*</span></label>
                  <input
                    type="text"
                    className={inputCls}
                    value={businessForm.invoice_postcode}
                    onChange={e => setBiz('invoice_postcode', e.target.value)}
                    placeholder={t('placeholder.postalCode')}
                  />
                </div>
                <div>
                  <label className={labelCls}>{t('label.city')} <span className="text-red-400">*</span></label>
                  <input
                    type="text"
                    className={inputCls}
                    value={businessForm.invoice_plaatsnaam}
                    onChange={e => setBiz('invoice_plaatsnaam', e.target.value)}
                    placeholder={t('placeholder.city')}
                  />
                </div>
              </div>
              <div>
                <label className={labelCls}>{t('label.iban')} <span className="text-red-400">*</span></label>
                <input
                  type="text"
                  className={inputCls}
                  value={businessForm.invoice_iban}
                  onChange={e => setBiz('invoice_iban', e.target.value.toUpperCase())}
                  placeholder={t('placeholder.iban')}
                />
              </div>

              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={handleSaveBusinessProfile}
                  disabled={businessSaving}
                  className="inline-flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold text-white transition-opacity disabled:opacity-60"
                  style={{ backgroundColor: '#0B1F3A' }}
                >
                  {businessSaving && <Loader2 size={14} className="animate-spin" />}
                  {businessSaving ? t('button.saving') : t('button.saveInvoicingDetails')}
                </button>
              </div>

            </>
          )}
        </div>
      </div>

      {/* Opslaan */}
      {activeTab !== 'factuur' && (
      <div className="flex justify-end">
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-semibold text-white transition-opacity disabled:opacity-60"
          style={{ backgroundColor: '#0B1F3A' }}
        >
          {saving ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle size={16} />}
          {saving ? t('button.saving') : t('button.saveChanges')}
        </button>
      </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Belagom static documents: algemene voorwaarden, privacyverklaring en het klachtenreglement
// (PDF gegenereerd uit client/src/data/klachtenreglement.json).
// ---------------------------------------------------------------------------
const BELAGOM_DOCUMENTS = [
  {
    id: 'algemene-voorwaarden',
    titleKey: 'documents.termsTitle',
    descKey: 'documents.termsDesc',
    icon: BookOpen,
    url: '/terms',
  },
  {
    id: 'klachtenreglement',
    titleKey: 'documents.complaintsTitle',
    descKey: 'documents.complaintsDesc',
    icon: Scale,
    url: klachtenreglement.pdf,
  },
  {
    id: 'privacyverklaring',
    titleKey: 'documents.privacyTitle',
    descKey: 'documents.privacyDesc',
    icon: ShieldCheck,
    url: '/privacy',
  },
];

const PROFESSIONAL_TYPE_LABELS: Record<string, string> = {
  loopbaancoach: 'Loopbaancoach',
  vitaliteitscoach: 'Vitaliteitscoach',
  burnout_coach: 'Burnout Coach',
  teamcoach: 'Teamcoach',
  financieel_coach: 'Financieel Coach',
  mediator: 'Mediator',
  psycholoog_coach: 'Psycholoog Coach',
  vertrouwenspersoon: 'Vertrouwenspersoon',
  bedrijfsmaatschappelijk_werker: 'Bedrijfsmaatschappelijk Werker',
};

const ContractenView: React.FC = () => {
  const { t, i18n } = useTranslation('belagom');
  const { data, isLoading } = useQuery({
    queryKey: ['professional-belagom-documents'],
    queryFn: async () => {
      const res = await api.get('/professional/my-documents');
      return res.data?.data as {
        screening: {
          status: string;
          kvk_uittreksel_url?: string;
          kvk_uittreksel_upload_date?: string;
          policy_document_url?: string;
          insurance_company?: string;
          policy_number?: string;
          submitted_at?: string;
        } | null;
        registrations: Array<{
          professional_type: string;
          registration_proof_url?: string;
          registration_number?: string;
          registration_organization?: string;
          is_approved: boolean;
        }>;
        signed_contract: {
          id: string;
          signed_at: string;
          contract_version: string;
          contract_pdf_url?: string;
        } | null;
        client_contracts: ClientContract[];
      };
    },
  });
  const [openContractId, setOpenContractId] = useState<string | null>(null);

  const resolveUrl = (url?: string | null) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    return url.startsWith('/') ? url : `/${url}`;
  };

  const fmtDate = (d?: string | null) =>
    d ? new Date(d).toLocaleDateString(i18n.language, { day: 'numeric', month: 'long', year: 'numeric' }) : null;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <p className="text-gray-500">{t('loading.documents')}</p>
      </div>
    );
  }

  const { screening, registrations = [], signed_contract, client_contracts = [] } = data || {};
  const certsWithProof = registrations.filter((r) => r.registration_proof_url);

  return (
    <div className="space-y-8">
      {/* ---- Section 1: Mijn geüploade documenten ---- */}
      <section>
        <h2 className="text-base font-semibold text-gray-700 mb-3 flex items-center gap-2">
          <FolderOpen size={18} className="text-[#0B1F3A]" />
          {t('section.uploadedDocuments')}
        </h2>

        {!screening ? (
          <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-6 text-center text-sm text-gray-500">
            {t('info.noDocuments')}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* KVK */}
            <DocumentCard
              title={t('label.kvkExtract')}
              subtitle={screening.kvk_uittreksel_upload_date ? `${t('label.uploadedDate')} ${fmtDate(screening.kvk_uittreksel_upload_date)}` : undefined}
              url={resolveUrl(screening.kvk_uittreksel_url)}
              approved={screening.status === 'approved'}
            />
            {/* Verzekering */}
            <DocumentCard
              title={t('label.insurancePolicy')}
              subtitle={screening.insurance_company ? `${screening.insurance_company}${screening.policy_number ? ` \u00b7 ${screening.policy_number}` : ''}` : undefined}
              url={resolveUrl(screening.policy_document_url)}
              approved={screening.status === 'approved'}
            />
            {/* Certificeringen */}
            {certsWithProof.map((r) => (
              <DocumentCard
                key={r.professional_type}
                title={`${t('label.certification')} ${PROFESSIONAL_TYPE_LABELS[r.professional_type] ?? r.professional_type}`}
                subtitle={r.registration_organization ?? r.registration_number ?? undefined}
                url={resolveUrl(r.registration_proof_url)}
                approved={r.is_approved}
              />
            ))}
          </div>
        )}
      </section>

      {/* ---- Section 2: Getekend contract ---- */}
      <section>
        <h2 className="text-base font-semibold text-gray-700 mb-3 flex items-center gap-2">
          <FileText size={18} className="text-[#0B1F3A]" />
          {t('section.signedContract')}
        </h2>

        {signed_contract ? (
          <DocumentCard
            title={t('label.cooperationAgreement')}
            subtitle={`${t('label.signedOn')} ${fmtDate(signed_contract.signed_at)} \u00b7 ${t('label.version')} ${signed_contract.contract_version}`}
            url={resolveUrl(signed_contract.contract_pdf_url)}
            approved
          />
        ) : (
          <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-6 text-center text-sm text-gray-500">
            <Clock size={20} className="mx-auto mb-2 text-gray-400" />
            {t('info.noSignedContract')}
          </div>
        )}
      </section>

      {/* ---- Section 2b: Contracten met opdrachtgevers ---- */}
      <section>
        <h2 className="text-base font-semibold text-gray-700 mb-3 flex items-center gap-2">
          <Briefcase size={18} className="text-[#0B1F3A]" />
          {t('section.clientContracts')}
        </h2>

        {client_contracts.length === 0 ? (
          <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-6 text-center text-sm text-gray-500">
            {t('info.noClientContracts')}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {client_contracts.map((c) => {
              const signed = c.status === 'GETEKEND';
              const kind = t(c.kind === 'project' ? 'label.projectAgreement' : 'label.poolAgreement');
              return (
                <div
                  key={c.id}
                  className="flex items-start gap-4 rounded-xl border border-gray-200 bg-white p-4 hover:shadow-sm transition-shadow"
                >
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 bg-blue-50">
                    <FileText size={20} className="text-[#0B1F3A]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-sm text-gray-900 truncate">
                        {c.employer_name ?? t('label.unknownEmployer')}
                      </p>
                      {signed && <CheckCircle size={14} className="text-blue-500 flex-shrink-0" />}
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5 truncate">
                      {c.subject ? `${kind} · ${c.subject}` : kind}
                    </p>
                    <p className={`text-xs mt-0.5 ${signed ? 'text-gray-500' : 'text-amber-700'}`}>
                      {signed
                        ? `${t('label.signedOn')} ${fmtDate(c.sealed_at)}`
                        : t(c.status === 'WACHT_OP_PROFESSIONAL' ? 'status.awaitingYourSignature' : 'status.awaitingEmployerSignature')}
                      {c.addenda_count > 0 && ` · ${t('label.addendaCount', { count: c.addenda_count })}`}
                    </p>
                    <button
                      type="button"
                      onClick={() => setOpenContractId(c.id)}
                      className="inline-flex items-center gap-1 mt-2 text-xs font-medium text-[#0B1F3A] hover:underline"
                    >
                      <ExternalLink size={12} />
                      {t('button.view')}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {openContractId && (
        <ClientContractModal contractId={openContractId} onClose={() => setOpenContractId(null)} />
      )}

      {/* ---- Section 3: Belagom documenten ---- */}
      <section>
        <h2 className="text-base font-semibold text-gray-700 mb-3 flex items-center gap-2">
          <BookOpen size={18} className="text-[#0B1F3A]" />
          {t('section.belagomDocuments')}
        </h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {BELAGOM_DOCUMENTS.map((doc) => {
            const Icon = doc.icon;
            return (
              <div
                key={doc.id}
                className="flex items-start gap-4 rounded-xl border border-gray-200 bg-white p-4 hover:shadow-sm transition-shadow"
              >
                <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 bg-blue-50">
                  <Icon size={20} className="text-[#0B1F3A]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm text-gray-900">{t(doc.titleKey)}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{t(doc.descKey)}</p>
                  <a
                    href={doc.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 mt-2 text-xs font-medium text-[#0B1F3A] hover:underline"
                  >
                    <ExternalLink size={12} />
                    {t('button.open')}
                  </a>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* ---- Section 4: Klachtenreglement & formulier ---- */}
      <KlachtenSectie formulierAchterKnop />

      <p className="text-xs text-gray-400">
        {t('info.renewDocuments')}
      </p>
    </div>
  );
};

interface ClientContract {
  id: string;
  kind: 'project' | 'pool';
  status: 'WACHT_OP_WERKGEVER' | 'WACHT_OP_PROFESSIONAL' | 'GETEKEND';
  employer_name: string | null;
  subject: string | null;
  created_at: string;
  sealed_at: string | null;
  addenda_count: number;
}

const ClientContractModal: React.FC<{ contractId: string; onClose: () => void }> = ({ contractId, onClose }) => {
  const { t, i18n } = useTranslation('belagom');
  const { data: contract, isLoading } = useQuery({
    queryKey: ['professional-client-contract', contractId],
    queryFn: async () => {
      const res = await api.get(`/professional/my-documents/client-contracts/${contractId}`);
      return res.data?.data as {
        status: string;
        contract_html: string;
        contract_sha256: string | null;
        sealed_at: string | null;
        werkgever_getekend_op: string | null;
        professional_getekend_op: string | null;
        addenda: Array<{ id: string; volgnummer: number; addendum_html: string; sealed_at: string }>;
      };
    },
  });

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const fmtDateTime = (d?: string | null) => (d ? new Date(d).toLocaleString(i18n.language) : '—');
  const contractClass = 'prose prose-sm max-w-none [&_table]:w-full [&_td]:border [&_td]:p-1 [&_th]:border [&_th]:p-1';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        className="relative w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-xl bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label={t('button.close')}
          className="absolute right-3 top-3 rounded-lg p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
        >
          <X size={18} />
        </button>

        {isLoading || !contract ? (
          <div className="flex items-center justify-center p-12 text-gray-500">
            <Loader2 size={20} className="animate-spin" />
          </div>
        ) : (
          <div className="p-6 space-y-6">
            <div className={contractClass} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(contract.contract_html) }} />

            {contract.addenda.map((a) => (
              <div key={a.id} className="border-t border-gray-200 pt-6">
                <p className="text-xs text-gray-500 mb-2">
                  {t('label.addendum', { number: a.volgnummer })} &middot; {fmtDateTime(a.sealed_at)}
                </p>
                <div className={contractClass} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(a.addendum_html) }} />
              </div>
            ))}

            {contract.status === 'GETEKEND' && (
              <div className="flex items-start gap-3 rounded-lg bg-green-50 p-4 text-sm text-green-800">
                <ShieldCheck size={20} className="flex-shrink-0" />
                <div>
                  <p className="font-semibold">{t('info.contractSealed')}</p>
                  <p>
                    {t('label.employerSigned')}: {fmtDateTime(contract.werkgever_getekend_op)} &middot;{' '}
                    {t('label.professionalSigned')}: {fmtDateTime(contract.professional_getekend_op)}
                  </p>
                  {contract.contract_sha256 && (
                    <p className="mt-1 break-all font-mono text-xs text-green-700">SHA-256: {contract.contract_sha256}</p>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

interface DocumentCardProps {
  title: string;
  subtitle?: string;
  url: string | null;
  approved?: boolean;
}

const DocumentCard: React.FC<DocumentCardProps> = ({ title, subtitle, url, approved }) => {
  const { t } = useTranslation('belagom');
  return (
  <div className="flex items-start gap-4 rounded-xl border border-gray-200 bg-white p-4 hover:shadow-sm transition-shadow">
    <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 bg-blue-50">
      <FileText size={20} className="text-[#0B1F3A]" />
    </div>
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2">
        <p className="font-semibold text-sm text-gray-900 truncate">{title}</p>
        {approved && <CheckCircle size={14} className="text-blue-500 flex-shrink-0" />}
      </div>
      {subtitle && <p className="text-xs text-gray-500 mt-0.5 truncate">{subtitle}</p>}
      {url ? (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 mt-2 text-xs font-medium text-[#0B1F3A] hover:underline"
        >
          <Download size={12} />
          {t('button.download')}
        </a>
      ) : (
        <span className="inline-flex items-center gap-1 mt-2 text-xs text-gray-400">
          <Clock size={12} />
          {t('status.notAvailable')}
        </span>
      )}
    </div>
  </div>
  );
};

// ---------------------------------------------------------------------------
// Notification settings view
// ---------------------------------------------------------------------------
const NOTIF_ROWS: Array<{ key: string; label: string; desc?: string }> = [
  { key: 'new_message', label: 'Nieuw bericht', desc: 'Iemand stuurt je een bericht' },
  { key: 'connection_request', label: 'Verbindingsverzoek', desc: 'Een opdrachtgever wil contact' },
  { key: 'dossier_update', label: 'Dossier update', desc: 'Wijziging in een klantdossier' },
  { key: 'invoice', label: 'Factuur', desc: 'Nieuwe of betaalde factuur' },
  { key: 'vacancy', label: 'Nieuwe opdracht', desc: 'Passende opdrachten in jouw regio' },
  { key: 'community', label: 'Community activiteit', desc: 'Reacties en likes op jouw berichten' },
];

const NotificationSettingsView: React.FC = () => {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['notification-preferences'],
    queryFn: async () => {
      const res = await api.get('/users/notification-preferences');
      return res.data.data as Record<string, boolean>;
    },
  });

  const saveMutation = useMutation({
    mutationFn: (prefs: Record<string, boolean>) => api.patch('/users/notification-preferences', prefs),
    onSuccess: () => {
      toast.success('Instellingen opgeslagen');
      queryClient.invalidateQueries({ queryKey: ['notification-preferences'] });
    },
    onError: () => toast.error('Fout bij opslaan'),
  });

  const toggle = (key: string) => {
    if (!data) return;
    const updated = { ...data, [key]: !data[key] };
    saveMutation.mutate(updated);
  };

  if (isLoading) return <div className="text-center py-8 text-gray-500">Laden...</div>;

  return (
    <div className="max-w-2xl space-y-6">
      <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
        Stel in via welke kanalen je meldingen wilt ontvangen. E-mail notificaties worden verstuurd naar jouw geregistreerde e-mailadres.
      </div>

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="grid grid-cols-3 text-xs font-semibold text-gray-500 uppercase tracking-wide bg-gray-50 px-6 py-3 border-b border-gray-200">
          <span className="col-span-1">Notificatie</span>
          <span className="text-center">E-mail</span>
          <span className="text-center">In-app</span>
        </div>
        <div className="divide-y divide-gray-100">
          {NOTIF_ROWS.map(({ key, label, desc }) => (
            <div key={key} className="grid grid-cols-3 px-6 py-4 items-center">
              <div>
                <p className="text-sm font-medium text-gray-900">{label}</p>
                {desc && <p className="text-xs text-gray-500 mt-0.5">{desc}</p>}
              </div>
              <div className="flex justify-center">
                <button
                  onClick={() => toggle(`email_${key}`)}
                  disabled={saveMutation.isPending}
                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                    data?.[`email_${key}`] ? 'bg-blue-600' : 'bg-gray-200'
                  }`}
                >
                  <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${
                    data?.[`email_${key}`] ? 'translate-x-4' : 'translate-x-0.5'
                  }`} />
                </button>
              </div>
              <div className="flex justify-center">
                <button
                  onClick={() => toggle(`inapp_${key}`)}
                  disabled={saveMutation.isPending}
                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                    data?.[`inapp_${key}`] ? 'bg-blue-600' : 'bg-gray-200'
                  }`}
                >
                  <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${
                    data?.[`inapp_${key}`] ? 'translate-x-4' : 'translate-x-0.5'
                  }`} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Stripe uitbetalingen (bankkoppeling)
// ---------------------------------------------------------------------------
const UitbetalingenView: React.FC = () => {
  const { t } = useTranslation('belagom');
  const [connecting, setConnecting] = useState(false);
  const [dashboardLoading, setDashboardLoading] = useState(false);

  const { data: stripeStatusData, refetch: refetchStripeStatus } = useQuery({
    queryKey: ['professional-stripe-status'],
    queryFn: async () => {
      const res = await api.get('/professional/stripe/status');
      return res.data?.data ?? { onboarding_status: 'not_started', capability_status_code: null, onboarding_url: null };
    },
  });

  const { data: businessProfileData } = useQuery({
    queryKey: ['professional-business-profile'],
    queryFn: async () => {
      const res = await api.get('/professional/business-profile');
      return res.data?.data ?? {};
    },
  });

  const statusLabels: Record<string, string> = {
    not_started: t('status.stripeNotStarted'),
    pending: t('status.stripePending'),
    verified: t('status.stripeVerified'),
    rejected: t('status.stripeRejected'),
  };

  const status = stripeStatusData?.onboarding_status || 'not_started';

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const res = await api.post('/professional/stripe/onboarding-link');
      const url = res.data?.data?.onboarding_url;
      toast.success(t('toast.stripeLinkCreated'));
      await refetchStripeStatus();
      // Account links zijn eenmalig en verlopen snel: meteen openen.
      if (url) window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(msg || t('toast.stripeLinkFailed'));
    } finally {
      setConnecting(false);
    }
  };

  const handleOpenDashboard = async () => {
    setDashboardLoading(true);
    try {
      const res = await api.post('/professional/stripe/dashboard-link');
      const url = res.data?.data?.dashboard_url;
      if (url) window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(msg || t('toast.stripeDashboardFailed'));
    } finally {
      setDashboardLoading(false);
    }
  };

  return (
    <div className="max-w-3xl space-y-6">
      <div>
        <div className="flex items-center gap-2 mb-1">
          <CreditCard size={18} className="text-[#0B1F3A]" />
          <h2 className="text-lg font-semibold text-gray-900">{t('section.payouts')}</h2>
        </div>
        <p className="text-sm text-gray-500">{t('info.payoutsHint')}</p>
      </div>

      {/* Stripe Connect status */}
      <div className="rounded-xl border border-gray-200 bg-white p-5 space-y-4">
        <div className="flex items-center gap-2">
          <ShieldCheck size={16} className="text-[#0B1F3A]" />
          <h3 className="text-sm font-semibold text-gray-800">{t('section.stripeConnection')}</h3>
        </div>
        <p className="text-xs text-gray-500">{t('info.stripeHint')}</p>

        <div className="flex flex-wrap items-center justify-between gap-3 p-4 border border-gray-200 rounded-lg">
          <div>
            <p className="text-xs font-semibold text-gray-600 mb-1">{t('label.stripeStatus')}</p>
            <span
              className={`inline-block px-2.5 py-1 rounded-full text-xs font-medium ${
                status === 'verified'
                  ? 'bg-green-100 text-green-700'
                  : status === 'rejected'
                  ? 'bg-red-100 text-red-700'
                  : status === 'pending'
                  ? 'bg-yellow-100 text-yellow-700'
                  : 'bg-gray-100 text-gray-600'
              }`}
            >
              {statusLabels[status]}
            </span>
            {stripeStatusData?.capability_status_code && status !== 'verified' && (
              <p className="mt-1 text-[11px] text-gray-500">{stripeStatusData.capability_status_code}</p>
            )}
          </div>
          <div className="flex items-center gap-2">
            {status === 'verified' && (
              <button
                type="button"
                onClick={handleOpenDashboard}
                disabled={dashboardLoading}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-gray-700 border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
              >
                {dashboardLoading && <Loader2 size={14} className="animate-spin" />}
                {t('button.openStripeDashboard')}
              </button>
            )}
            <button
              type="button"
              onClick={handleConnect}
              disabled={connecting || status === 'verified'}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-[#0B1F3A] border border-[#0B1F3A] hover:bg-blue-50 disabled:opacity-50"
            >
              {connecting && <Loader2 size={14} className="animate-spin" />}
              {stripeStatusData?.onboarding_url && status !== 'verified'
                ? t('button.resumeStripeOnboarding')
                : t('button.connectStripe')}
            </button>
          </div>
        </div>
      </div>

      {/* IBAN — beheerd bij de factuurgegevens in het zakelijk portfolio */}
      <div className="rounded-xl border border-gray-200 bg-white p-5">
        <div className="flex items-center gap-2 mb-1">
          <FileText size={16} className="text-[#0B1F3A]" />
          <h3 className="text-sm font-semibold text-gray-800">{t('label.bankAccount')}</h3>
        </div>
        <p className="text-xs text-gray-500 mb-3">{t('info.ibanHint')}</p>
        <p className="font-mono text-sm text-gray-900">
          {businessProfileData?.invoice_iban || <span className="font-sans text-gray-400">{t('status.notAvailable')}</span>}
        </p>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Mijn Pools & Tarieven (jaarlijkse evaluatie, Artikel 3.4)
// ---------------------------------------------------------------------------

/** Pool-overeenkomsten lopen 12 maanden; 30 dagen voor het einde opent de heronderhandeling. */
const POOL_TERM_MONTHS = 12;
const POOL_RENEGOTIATION_WINDOW_DAYS = 30;

interface PoolConnection {
  id: number;
  status: string;
  employer_company_name?: string | null;
  employer_first_name?: string | null;
  employer_last_name?: string | null;
  agreement_id?: number | null;
  agreement_approved_at?: string | null;
  agreement_hourly_rate?: string | number | null;
  agreement_effective_hourly_rate?: string | number | null;
}

const experienceLevelFromKvkYears = (y: number): '0-2' | '2-5' | '5-10' | '10+' => {
  if (y >= 10) return '10+';
  if (y >= 5) return '5-10';
  if (y >= 2) return '2-5';
  return '0-2';
};

// ---------------------------------------------------------------------------
// Pool-uitnodigingen (Fase B): openingsbod → akkoord / tegenvoorstel / afwijzen,
// en de SMS-2FA-tegentekening van de Model-OvO zodra beide partijen akkoord zijn.
// ---------------------------------------------------------------------------

interface PoolOnderhandeling {
  id: string;
  status: 'REQUEST_SENT' | 'COUNTER_PROPOSAL' | 'AGREED' | 'CONTRACT_PENDING' | 'ACTIVE' | 'REJECTED' | 'WITHDRAWN';
  professional_type: string;
  discipline_label?: string | null;
  employer_company_name?: string | null;
  employer_first_name?: string | null;
  employer_last_name?: string | null;
  vestiging_naam?: string | null;
  brevet?: number | null;
  brevet_label?: string | null;
  bandbreedte_min?: number | null;
  bandbreedte_max?: number | null;
  tariefmodus?: 'MARKTCONFORM' | 'GROEP' | 'INDIVIDUEEL';
  bod_werkgever: number | null;
  tegenbod_professional?: number | null;
  akkoord_tarief?: number | null;
  huidig_bod: number | null;
  huidig_bod_door: 'opdrachtgever' | 'professional' | 'systeem';
  bericht?: string | null;
  laatste_actie_tekst?: string | null;
  laatste_actie_op?: string | null;
}

const PoolUitnodigingenSection: React.FC = () => {
  const { t } = useTranslation('belagom');
  const queryClient = useQueryClient();
  const [tegenvoorstelId, setTegenvoorstelId] = useState<string | null>(null);
  const [tegenvoorstelTarief, setTegenvoorstelTarief] = useState('');
  const [tegenvoorstelToelichting, setTegenvoorstelToelichting] = useState('');
  const [tekenId, setTekenId] = useState<string | null>(null);
  const [smsCode, setSmsCode] = useState('');
  const [smsHint, setSmsHint] = useState<string | null>(null);

  const { data: onderhandelingen = [], isLoading } = useQuery<PoolOnderhandeling[]>({
    queryKey: ['pool-onderhandelingen-professional'],
    queryFn: async () => {
      const res = await api.get('/pool/professional/onderhandelingen');
      return res.data?.data ?? [];
    },
    refetchInterval: 60_000,
  });

  const reageerMutation = useMutation({
    mutationFn: async (payload: { id: string; actie: 'akkoord' | 'tegenvoorstel' | 'afwijzen'; tarief?: number; toelichting?: string }) => {
      const res = await api.post(`/pool/professional/onderhandelingen/${payload.id}/reageer`, {
        actie: payload.actie,
        tarief: payload.tarief,
        toelichting: payload.toelichting,
      });
      return res.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['pool-onderhandelingen-professional'] });
      if (variables.actie === 'akkoord') toast.success(t('pool.acceptedSuccess'));
      else if (variables.actie === 'tegenvoorstel') toast.success(t('pool.counterSuccess'));
      else toast.success(t('pool.rejectedSuccess'));
      setTegenvoorstelId(null);
      setTegenvoorstelTarief('');
      setTegenvoorstelToelichting('');
    },
    onError: (err: any) => toast.error(err?.response?.data?.message || t('pool.actionError')),
  });

  const tekenCodeMutation = useMutation({
    mutationFn: async (id: string) => (await api.post(`/pool/onderhandelingen/${id}/contract/teken-code`)).data,
    onSuccess: (data) => {
      setSmsHint(data?.data?.telefoon_hint ?? null);
      toast.success(t('pool.smsCodeSent'));
    },
    onError: (err: any) => toast.error(err?.response?.data?.message || t('pool.actionError')),
  });

  const ondertekenMutation = useMutation({
    mutationFn: async (payload: { id: string; code: string }) =>
      (await api.post(`/pool/onderhandelingen/${payload.id}/contract/onderteken`, { code: payload.code })).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pool-onderhandelingen-professional'] });
      queryClient.invalidateQueries({ queryKey: ['professional-connections'] });
      toast.success(t('pool.signedSuccess'));
      setTekenId(null);
      setSmsCode('');
      setSmsHint(null);
    },
    onError: (err: any) => toast.error(err?.response?.data?.message || t('pool.actionError')),
  });

  const zichtbaar = onderhandelingen.filter((o) => o.status !== 'REJECTED' && o.status !== 'WITHDRAWN' && o.status !== 'ACTIVE');
  if (!isLoading && zichtbaar.length === 0) return null;

  const werkgeverNaam = (o: PoolOnderhandeling) =>
    o.employer_company_name || [o.employer_first_name, o.employer_last_name].filter(Boolean).join(' ') || t('pool.unknownEmployer');

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-5 space-y-4">
      <div className="flex items-center gap-2">
        <MessageSquare size={16} className="text-[#0B1F3A]" />
        <h3 className="text-sm font-semibold text-gray-800">{t('pool.invitationsTitle')}</h3>
      </div>

      {isLoading ? (
        <p className="text-sm text-gray-400">{t('common:common.loading')}</p>
      ) : (
        <div className="space-y-4">
          {zichtbaar.map((o) => {
            const kanReageren = o.status === 'REQUEST_SENT';
            // Marktconforme uitvraag: geen bod van de opdrachtgever — de professional bepaalt het starttarief
            const tariefOpen = kanReageren && o.bod_werkgever == null;
            const wachtOpWerkgever = o.status === 'COUNTER_PROPOSAL';
            const wachtOpOndertekening = o.status === 'CONTRACT_PENDING';
            const band = o.bandbreedte_max
              ? `€${Number(o.bandbreedte_min).toFixed(2)} – €${Number(o.bandbreedte_max).toFixed(2)}`
              : o.bandbreedte_min
              ? `${t('pool.from')} €${Number(o.bandbreedte_min).toFixed(2)}`
              : null;

            return (
              <div key={o.id} className="rounded-lg border border-gray-200 p-4 space-y-3">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-900 truncate">{werkgeverNaam(o)}</p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {o.discipline_label} {o.vestiging_naam ? `· ${o.vestiging_naam}` : ''}
                    </p>
                    {o.brevet_label && (
                      <p className="text-xs text-gray-400 mt-0.5">
                        {o.brevet_label}
                        {band ? ` · ${t('pool.marketRange')}: ${band}` : ''}
                      </p>
                    )}
                  </div>
                  <span
                    className={`flex-shrink-0 text-xs font-medium px-2.5 py-1 rounded-full ${
                      wachtOpOndertekening
                        ? 'bg-blue-100 text-blue-800'
                        : wachtOpWerkgever
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-gray-100 text-gray-700'
                    }`}
                  >
                    {t(`pool.status.${o.status}`)}
                  </span>
                </div>

                {o.huidig_bod == null ? (
                  <p className="text-sm text-gray-700">
                    <span className="font-semibold text-[#0B1F3A]">{t('pool.marketRateRequest')}</span>{' '}
                    <span className="text-gray-500">{t('pool.marketRateRequestHint')}</span>
                  </p>
                ) : (
                  <p className="text-sm text-gray-700">
                    {t('pool.currentBid')}: <span className="font-bold text-[#0B1F3A]">&euro;{Number(o.huidig_bod).toFixed(2)}</span>{' '}
                    {t('info.perHour')}
                    {o.huidig_bod_door === 'professional' && ` · ${t('pool.yourCounterOffer')}`}
                  </p>
                )}
                {o.bericht && <p className="text-xs text-gray-500 italic">&ldquo;{o.bericht}&rdquo;</p>}

                {kanReageren && tegenvoorstelId !== o.id && (
                  <div className="flex flex-wrap gap-2">
                    {!tariefOpen && (
                      <button
                        type="button"
                        onClick={() => reageerMutation.mutate({ id: o.id, actie: 'akkoord' })}
                        disabled={reageerMutation.isPending}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-green-600 text-white text-xs font-semibold hover:bg-green-700 disabled:opacity-50"
                      >
                        <ThumbsUp size={14} /> {t('pool.acceptDirect')}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        setTegenvoorstelId(o.id);
                        setTegenvoorstelTarief(o.huidig_bod != null ? String(o.huidig_bod) : o.bandbreedte_min != null ? String(o.bandbreedte_min) : '');
                      }}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold ${
                        tariefOpen ? 'bg-[#0B1F3A] text-white hover:bg-[#1A3459]' : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <MessageSquare size={14} /> {tariefOpen ? t('pool.acceptWithRate') : t('pool.makeCounter')}
                    </button>
                    <button
                      type="button"
                      onClick={() => reageerMutation.mutate({ id: o.id, actie: 'afwijzen' })}
                      disabled={reageerMutation.isPending}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-red-200 text-red-700 text-xs font-semibold hover:bg-red-50 disabled:opacity-50"
                    >
                      <ThumbsDown size={14} /> {t('pool.reject')}
                    </button>
                  </div>
                )}

                {tegenvoorstelId === o.id && (
                  <div className="rounded-lg bg-gray-50 border border-gray-200 p-3 space-y-2">
                    <label className="block text-xs font-medium text-gray-600">{t('pool.yourRatePerHour')}</label>
                    <input
                      type="number"
                      step="0.01"
                      min={0}
                      value={tegenvoorstelTarief}
                      onChange={(e) => setTegenvoorstelTarief(e.target.value)}
                      className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm"
                    />
                    <textarea
                      value={tegenvoorstelToelichting}
                      onChange={(e) => setTegenvoorstelToelichting(e.target.value)}
                      placeholder={t('pool.toelichtingPlaceholder')}
                      rows={2}
                      className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm"
                    />
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() =>
                          reageerMutation.mutate({
                            id: o.id,
                            actie: 'tegenvoorstel',
                            tarief: parseFloat(tegenvoorstelTarief),
                            toelichting: tegenvoorstelToelichting || undefined,
                          })
                        }
                        disabled={reageerMutation.isPending || !tegenvoorstelTarief}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#0B1F3A] text-white text-xs font-semibold hover:bg-[#1A3459] disabled:opacity-50"
                      >
                        <Send size={14} /> {tariefOpen ? t('pool.sendRate') : t('pool.sendCounter')}
                      </button>
                      <button
                        type="button"
                        onClick={() => setTegenvoorstelId(null)}
                        className="px-3 py-1.5 rounded-lg text-xs font-semibold text-gray-500 hover:bg-gray-100"
                      >
                        {t('common:common.cancel')}
                      </button>
                    </div>
                  </div>
                )}

                {wachtOpWerkgever && (
                  <p className="text-xs text-gray-400">{o.bod_werkgever == null ? t('pool.waitingForEmployerRate') : t('pool.waitingForEmployer')}</p>
                )}

                {wachtOpOndertekening && tekenId !== o.id && (
                  <button
                    type="button"
                    onClick={() => {
                      setTekenId(o.id);
                      setSmsHint(null);
                      setSmsCode('');
                      tekenCodeMutation.mutate(o.id);
                    }}
                    disabled={tekenCodeMutation.isPending}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#0B1F3A] text-white text-xs font-semibold hover:bg-[#1A3459] disabled:opacity-50"
                  >
                    <KeyRound size={14} /> {t('pool.signContract')}
                  </button>
                )}

                {wachtOpOndertekening && tekenId === o.id && (
                  <div className="rounded-lg bg-gray-50 border border-gray-200 p-3 space-y-2">
                    <p className="text-xs text-gray-600">
                      {smsHint ? t('pool.smsCodeHint', { hint: smsHint }) : t('pool.smsCodeSending')}
                    </p>
                    <input
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      value={smsCode}
                      onChange={(e) => setSmsCode(e.target.value.replace(/\D/g, ''))}
                      placeholder="000000"
                      className="w-32 rounded-md border border-gray-300 px-3 py-1.5 text-sm tracking-widest"
                    />
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => ondertekenMutation.mutate({ id: o.id, code: smsCode })}
                        disabled={ondertekenMutation.isPending || smsCode.length !== 6}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-green-600 text-white text-xs font-semibold hover:bg-green-700 disabled:opacity-50"
                      >
                        <CheckCircle size={14} /> {t('pool.confirmSign')}
                      </button>
                      <button
                        type="button"
                        onClick={() => setTekenId(null)}
                        className="px-3 py-1.5 rounded-lg text-xs font-semibold text-gray-500 hover:bg-gray-100"
                      >
                        {t('common:common.cancel')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const PoolsTarievenView: React.FC = () => {
  const { t, i18n } = useTranslation('belagom');

  const { data: profile, isLoading: profileLoading } = useQuery({
    queryKey: ['professional-profile-belagom'],
    queryFn: async () => {
      const res = await api.get('/professional/profile');
      const data = res.data?.data;
      return data?.profile ?? data;
    },
  });

  const screeningTypes: string[] = Array.isArray(profile?.professional_types_from_screening)
    ? profile.professional_types_from_screening
    : [];

  const { data: groupedRatesData } = useQuery({
    queryKey: ['professional-rates-grouped-belagom'],
    queryFn: async () => {
      const response = await api.get('/professional-rates/grouped');
      return response.data.data;
    },
    enabled: screeningTypes.length > 0,
  });

  const { data: pools = [], isLoading: poolsLoading } = useQuery<PoolConnection[]>({
    queryKey: ['professional-connections'],
    queryFn: async () => {
      const res = await api.get('/professional/connections');
      return res.data?.data ?? [];
    },
  });

  const kvkYears = (() => {
    if (!profile?.kvk_registration_date) return 0;
    const reg = new Date(profile.kvk_registration_date);
    const now = new Date();
    let y = now.getFullYear() - reg.getFullYear();
    const m = now.getMonth() - reg.getMonth();
    if (m < 0 || (m === 0 && now.getDate() < reg.getDate())) y--;
    return Math.max(0, y);
  })();
  const kvkLevel = experienceLevelFromKvkYears(kvkYears);

  const expLevelLabels: Record<string, string> = {
    '0-2': t('constant.experienceLevel.starter'),
    '2-5': t('constant.experienceLevel.experienced'),
    '5-10': t('constant.experienceLevel.advanced'),
    '10+': t('constant.experienceLevel.expert'),
  };

  const fmtDate = (d: string) =>
    new Date(d).toLocaleDateString(i18n.language, { day: 'numeric', month: 'long', year: 'numeric' });

  /** Einddatum + resterende dagen van een pool-overeenkomst (art. 3.4). */
  const evaluationInfo = (approvedAt?: string | null) => {
    if (!approvedAt) return null;
    const end = new Date(approvedAt);
    end.setMonth(end.getMonth() + POOL_TERM_MONTHS);
    const daysLeft = Math.ceil((end.getTime() - Date.now()) / 86_400_000);
    return { end, daysLeft, isOpen: daysLeft <= POOL_RENEGOTIATION_WINDOW_DAYS };
  };

  const activePools = pools.filter(p => p.status === 'active');
  const richttarief = profile?.hourly_rate != null ? Number(profile.hourly_rate) : null;

  if (profileLoading) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <Loader2 size={20} className="animate-spin text-[#0B1F3A]" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl space-y-6">
      <div>
        <div className="flex items-center gap-2 mb-1">
          <Coins size={18} className="text-[#0B1F3A]" />
          <h2 className="text-lg font-semibold text-gray-900">{t('section.poolsAndRates')}</h2>
        </div>
        <p className="text-sm text-gray-500">{t('info.poolsHint')}</p>
      </div>

      {/* Richttarief */}
      <div className="rounded-xl border border-gray-200 bg-white p-5 space-y-4">
        <div className="flex items-center gap-2">
          <Info size={16} className="text-[#0B1F3A]" />
          <h3 className="text-sm font-semibold text-gray-800">{t('label.guidingRate')}</h3>
        </div>

        {richttarief != null && richttarief > 0 ? (
          <p className="text-3xl font-bold text-[#0B1F3A]">&euro;{richttarief.toFixed(2)}<span className="text-sm font-normal text-gray-500"> {t('info.perHour')}</span></p>
        ) : (
          <p className="text-sm text-gray-400">{t('status.notAvailable')}</p>
        )}

        {screeningTypes.length > 0 && profile?.kvk_registration_date && groupedRatesData && (
          <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-sm font-semibold text-green-800 mb-3">
              {t('section.marketRateAdvice')} ({expLevelLabels[kvkLevel]}, {kvkYears} {t('info.kvkYears')})
            </p>
            <div className="space-y-3">
              {screeningTypes.map(type => {
                const td = groupedRatesData.find((g: { type: string }) => g.type === type);
                const r = td?.rates?.find((x: { experience_level: string }) => x.experience_level === kvkLevel);
                if (!r) return null;
                const base = Number(r.base_rate);
                const final = Number(r.final_rate);
                return (
                  <div key={type} className="bg-white rounded-lg border border-green-100 p-3">
                    <p className="text-xs font-semibold text-gray-900 mb-2">{PROFESSIONAL_TYPE_LABELS[type] || type}</p>
                    <div className="grid grid-cols-3 gap-2 text-xs">
                      <div className="bg-green-50 rounded p-2">
                        <div className="text-gray-600">{t('info.yourRate')}</div>
                        <div className="text-lg font-bold text-green-700">&euro;{base.toFixed(2)}</div>
                      </div>
                      <div className="bg-blue-50 rounded p-2">
                        <div className="text-gray-600">{t('info.belagomFee')}</div>
                        <div className="text-lg font-bold text-[#0B1F3A]">&euro;{(final - base).toFixed(2)}</div>
                      </div>
                      <div className="bg-gray-50 rounded p-2">
                        <div className="text-gray-600">{t('info.customerPays')}</div>
                        <div className="text-lg font-bold text-gray-900">&euro;{final.toFixed(2)}</div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <p className="text-xs text-gray-500 mt-3">{t('info.rateScaling')}</p>
          </div>
        )}
      </div>

      {/* Pool-uitnodigingen: openingsbod, tegenvoorstel, SMS-2FA-tegentekening */}
      <PoolUitnodigingenSection />

      {/* Actieve pools */}
      <div className="rounded-xl border border-gray-200 bg-white p-5 space-y-4">
        <div className="flex items-center gap-2">
          <Briefcase size={16} className="text-[#0B1F3A]" />
          <h3 className="text-sm font-semibold text-gray-800">
            {t('label.activePools', { count: activePools.length })}
          </h3>
        </div>

        {poolsLoading ? (
          <p className="text-sm text-gray-400">{t('common:common.loading')}</p>
        ) : activePools.length === 0 ? (
          <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50 p-6 text-center text-sm text-gray-400">
            {t('info.noPools')}
          </div>
        ) : (
          <div className="space-y-3">
            {activePools.map(pool => {
              const evalInfo = evaluationInfo(pool.agreement_approved_at);
              const rate = pool.agreement_effective_hourly_rate ?? pool.agreement_hourly_rate;
              const rateNum = rate != null ? Number(rate) : 0;
              const employer =
                pool.employer_company_name ||
                [pool.employer_first_name, pool.employer_last_name].filter(Boolean).join(' ');
              return (
                <div key={pool.id} className="rounded-lg border border-gray-200 p-4">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-gray-900 truncate">{employer}</p>
                      {rateNum > 0 && (
                        <p className="text-xs text-gray-500 mt-0.5">
                          {t('label.poolRate')}: <span className="font-semibold text-gray-800">&euro;{rateNum.toFixed(2)}</span> {t('info.perHour')}
                        </p>
                      )}
                    </div>
                    {evalInfo && (
                      <span
                        className={`flex-shrink-0 text-xs font-medium px-2.5 py-1 rounded-full ${
                          evalInfo.daysLeft < 0
                            ? 'bg-red-100 text-red-700'
                            : evalInfo.isOpen
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-green-100 text-green-700'
                        }`}
                      >
                        {evalInfo.daysLeft < 0
                          ? t('status.poolExpired')
                          : evalInfo.isOpen
                          ? t('status.poolRenegotiation', { days: evalInfo.daysLeft })
                          : t('status.poolRunning', { days: evalInfo.daysLeft })}
                      </span>
                    )}
                  </div>

                  {evalInfo && (
                    <p className="text-xs text-gray-400 mt-2">
                      {t('label.poolEndDate')}: {fmtDate(evalInfo.end.toISOString())}
                    </p>
                  )}

                  {pool.agreement_id && (
                    <Link
                      to={`/professional/connections/${pool.id}/agreements`}
                      className="inline-flex items-center gap-1.5 mt-3 text-xs font-semibold text-[#0B1F3A] hover:underline"
                    >
                      {evalInfo?.isOpen ? t('button.startPoolEvaluation') : t('button.viewPoolAgreement')}
                      <ExternalLink size={12} />
                    </Link>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <p className="text-xs text-gray-400 leading-relaxed">{t('info.article34')}</p>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// View renderer
// ---------------------------------------------------------------------------
const VIEW_COMPONENTS: Record<BelagomView, React.FC> = {
  screening: ScreeningOverviewView,
  uitbetalingen: UitbetalingenView,
  portfolio: PortfolioView,
  pools: PoolsTarievenView,
  contracten: ContractenView,
  crisis: ProfessionalCrisisPool,
  vertrouwenspersoon: ProfessionalWaakvlam,
  notificaties: NotificationSettingsView,
};

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
export const ProfessionalBelagomPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const validViews: BelagomView[] = ['screening', 'uitbetalingen', 'portfolio', 'pools', 'contracten', 'crisis', 'vertrouwenspersoon', 'notificaties'];
  /** Vertaalt een ?view=-parameter (incl. oude waardes) naar een geldige view. */
  const resolveView = (raw: string | null): BelagomView | null => {
    if (!raw) return null;
    if ((validViews as string[]).includes(raw)) return raw as BelagomView;
    return LEGACY_BELAGOM_VIEWS[raw] ?? null;
  };
  const initialView: BelagomView = resolveView(searchParams.get('view')) ?? 'screening';
  const [activeView, setActiveView] = useState<BelagomView>(initialView);

  useEffect(() => {
    const v = resolveView(searchParams.get('view'));
    // Gelijke waarde = no-op in React, dus activeView hoeft niet vergeleken te worden.
    if (v) setActiveView(v);
    // resolveView is puur (alleen validViews/LEGACY_BELAGOM_VIEWS).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleViewChange = (view: BelagomView) => {
    setActiveView(view);
    setSearchParams({ view }, { replace: true });
  };

  const ActiveView = VIEW_COMPONENTS[activeView];

  return (
    <ProfessionalBelagomLayout activeView={activeView} onViewChange={handleViewChange}>
      {/* View content */}
      <div className="p-8">
        <ActiveView />
      </div>
    </ProfessionalBelagomLayout>
  );
};
